import { useEffect, useState } from "react";
import { useAtom } from "jotai";
import { AppStateAtom } from "../../../store";
import { checkToken, login } from "../../../api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GetGoogleOAuthLogin } from "../../../api/GetGoogleOAuthLogin";
import { useUser } from "../../../hooks/useUser";
import { useToast } from "@chakra-ui/react";
import {
  track,
  identify,
  alias,
  setUserProperties,
  EVENTS,
} from "../../../utils/analytics";
import { mix } from "framer-motion";

interface LoginController {
  email: string;
  formPasswordError: string;
  formEmailError: string;
  errorMessage: string;
  loading: boolean;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  login: () => void;
}

export const useLoginController = () => {
  const [appState, setAppState] = useAtom(AppStateAtom);
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [formEmailError, setFormEmailError] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [formPasswordError, setFormPasswordError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useUser();
  const toast = useToast();

  const navigate = useNavigate();

  useEffect(() => {
    let code = searchParams.get("code");
    let scope = searchParams.get("scope");

    if (code && scope) {
      setLoading(true);
      GetGoogleOAuthLogin(code, scope).then((data) => {
        track(EVENTS.AUTH.GOOGLE_LOGIN, {
          email: data.email,
          success: data.success,
        });

        identify(data.user.email);
        alias(data.user.email);

        if (data.success === true) {
          window.localStorage.setItem("token", data.token);
          window.location.href = "/app/dashboard";
        }
      });
    }
  }, []);

  const peformLogin = async () => {
    track(EVENTS.AUTH.LOGIN_ATTEMPT, {
      email: email,
    });

    setLoading(true);
    user.setLoading(true);

    setTimeout(() => {
      login(email, password)
        .then((res) => {
          setFormEmailError("");
          setFormPasswordError("");

          if (res.success === true) {
            identify(res.user.email);
            alias(res.user.email);
            setUserProperties({
              $email: res.user.email,
              $name: res.user.email.split("@")[0],
            });
            track(EVENTS.AUTH.LOGIN_SUCCESS, {
              email: res.user.email,
            });

            user.setLoginSettings(email, res.user.gridTheme, res.token);
            setTimeout(() => {
              navigate("/app/dashboard");
              setLoading(false);
            }, 200);
          } else {
            if (res.success === false) {
              track(EVENTS.AUTH.LOGIN_FAILURE, {
                email: email,
              });

              if (res.errors) {
                res.errors.filter((e: any) => {
                  if (e.path === "email") {
                    setFormEmailError(e.msg);
                    toast({
                      title: "Error",
                      description: e.msg,
                      status: "error",
                      duration: 4000,
                      isClosable: true,
                    });
                  }
                  if (e.path === "password") {
                    setFormPasswordError(e.msg);
                    toast({
                      title: "Error",
                      description: e.msg,
                      status: "error",
                      duration: 4000,
                      isClosable: true,
                    });
                  }
                });
              } else {
                track(EVENTS.AUTH.LOGIN_FAILURE, {
                  email: email,
                  error: res.message,
                });

                setErrorMessage(res.message);
                toast({
                  title: "Error",
                  description: res.message,
                  status: "error",
                  duration: 4000,
                  isClosable: true,
                });
              }
              setLoading(false);
            }
            user.logUserOut();
          }
        })
        .catch((e: any) => {
          track(EVENTS.AUTH.LOGIN_FAILURE, {
            email: email,
            error: e.message,
          });

          console.log("API Call, error call login", e);
          user.logUserOut();
          setLoading(false);
        });
    }, 200);
  };

  useEffect(() => {
    checkIfAlreadyLoggedIn();
  }, []);

  const checkIfAlreadyLoggedIn = async () => {
    const data = await checkToken();
    if (data.success === true) {
      navigate("/app/dashboard");
    }
  };

  return {
    email,
    setEmail,
    password,
    loading,
    setPassword,
    errorMessage,
    login: peformLogin,
    formEmailError,
    formPasswordError,
  } as LoginController;
};
